import React, { useRef, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

/**
 * ParticlesBackground — subtle grid + floating dots.
 * - Public pages: grid + dots
 * - Dashboard: grid only (no dots)
 * - Admin: hidden entirely
 */

const HIDDEN_PREFIXES = ['/admin', '/privacy', '/terms', '/blog', '/features', '/advertise', '/success-stories', '/embed'];
const GRID_ONLY_PREFIXES = ['/dashboard', '/settings', '/chat', '/inbox'];

export default function ParticlesBackground() {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const [mode, setMode] = useState('full'); // 'full' | 'grid' | 'hidden'

  const { pathname } = useLocation();

  const { theme } = useTheme();
  const isDark = theme === 'dark';

  useEffect(() => {
    if (HIDDEN_PREFIXES.some(p => pathname.startsWith(p))) {
      setMode('hidden');
    } else if (GRID_ONLY_PREFIXES.some(p => pathname.startsWith(p))) {
      setMode('grid');
    } else {
      setMode('full');
    }
  }, [pathname]);

  useEffect(() => {
    if (mode === 'hidden' || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width, height;

    const GRID_SIZE = 48;
    const DOT_COUNT = 35;
    const showDots = mode === 'full';
    const dots = [];

    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    // Init dots
    const initDots = () => {
      dots.length = 0;
      if (!showDots) return;
      for (let i = 0; i < DOT_COUNT; i++) {
        dots.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          radius: Math.random() * 1.5 + 0.5,
          opacity: Math.random() * 0.3 + 0.05,
        });
      }
    };

    resize();
    initDots();

    const handleResize = () => { resize(); initDots(); };
    window.addEventListener('resize', handleResize);

    const draw = () => {
      ctx.clearRect(0, 0, width, height);



      // Draw and update dots (only in full mode)
      if (showDots) {
        for (const dot of dots) {
          dot.x += dot.vx;
          dot.y += dot.vy;

          if (dot.x < 0) dot.x = width;
          if (dot.x > width) dot.x = 0;
          if (dot.y < 0) dot.y = height;
          if (dot.y > height) dot.y = 0;

          const dotColor = isDark
            ? `rgba(255, 255, 255, ${dot.opacity})`
            : `rgba(0, 0, 0, ${dot.opacity * 1.2})`;
          ctx.beginPath();
          ctx.arc(dot.x, dot.y, dot.radius, 0, Math.PI * 2);
          ctx.fillStyle = dotColor;
          ctx.fill();
        }
      }

      animRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [mode, isDark]);

  if (mode === 'hidden') return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
      data-testid="particles-background"
    />
  );
}
